import type { SyncPush } from '@revival/contracts';
import { api, ApiError } from './api.js';
import { deleteAuditDraft, listAuditDrafts, loadAuditDraft, saveAuditDraft } from './audit-draft.js';
import { cachedValue, cacheValue, isNetworkFailure } from './offline-cache.js';
import type { Bootstrap } from './api.js';
import type { CommentFile } from './comment-files.js';

export type QueuedChecklist = {
  version: 3; ownerId: string; projectId: string; projectName: string;
  submission: SyncPush; draft: unknown;
  media: Array<{ photo: File | null; video: File | null; photoClientId: string; videoClientId: string; drawingNote?: string }>;
  state: 'pending' | 'failed'; message: string;
  attempts?: number; retryAt?: number;
};
export type QueuedSnagAction = {
  version: 4 | 5; ownerId: string; projectId: string; projectName: string; snagId: string; reference: string;
  queuedAt?: string; actionId: string; state: 'pending' | 'failed'; message: string; attempts?: number; retryAt?: number;
  submission: SyncPush;
  media: Array<{ file: File; clientId: string; kind: 'completion' | 'manager_close_off' | 'issue'; capturedAt: string }>;
  comment?: { text: string; clientId: string; attachments?: CommentFile[] };
  pin?: {drawingId:string;drawingPage:number;markupX:number;markupY:number;expectedVersion:number};
};
export type QueuedWork = QueuedChecklist | QueuedSnagAction;
export async function queuedWork(ownerId: string) {
  return (await listAuditDrafts<QueuedWork>()).filter(item => [3, 4, 5].includes(item.payload.version) && item.payload.ownerId === ownerId).sort((a, b) => (a.payload.version !== 3 ? a.payload.queuedAt ?? a.savedAt : a.savedAt).localeCompare(b.payload.version !== 3 ? b.payload.queuedAt ?? b.savedAt : b.savedAt));
}
export async function queueSnagAction(item: QueuedSnagAction) {
  if (api.isViewingAs() && item.ownerId !== api.workOwner(api.currentOwner())) throw new ApiError("The sending account changed. Reopen this account to save its work.", 403);
  const key = `action:${item.ownerId}:${item.actionId}`;
  if (!await loadAuditDraft(key)) await saveAuditDraft(key, {...item,queuedAt:item.queuedAt ?? new Date().toISOString()});
  window.dispatchEvent(new Event('snag-queue-changed'));
  window.dispatchEvent(new Event('snag-queue-added'));
  return key;
}
export const queueKey = (item: QueuedChecklist) => `issue:${item.ownerId}:${item.submission.operations[0]!.operationId}`;
export async function queueChecklist(item: QueuedChecklist) {
  if (api.isViewingAs() && item.ownerId !== api.workOwner(api.currentOwner())) throw new ApiError("The sending account changed. Reopen this account to save its work.", 403);
  const key = queueKey(item);
  const existing=await loadAuditDraft<QueuedChecklist>(key);
  if(existing) return key;
  await saveAuditDraft(key,item);
  window.dispatchEvent(new Event('snag-queue-changed'));
  window.dispatchEvent(new Event('snag-queue-added'));
  return key;
}
export async function queuedChecklists(ownerId: string) {
  return (await listAuditDrafts<QueuedChecklist>()).filter((item) => item.payload.version === 3 && item.payload.ownerId === ownerId);
}
// Separate server confirmation from presentation refresh. Retry always uses the
// same operation and media IDs, even after losing a response or closing the form.
export async function issueChecklist(item: QueuedChecklist, client: Pick<typeof api,'push'|'uploadEvidence'|'snagComments'> = api, active: () => boolean = () => true) {
  const check = () => {if(!active())throw new ApiError("The sending account changed. Sign in with the original account to continue.",401);};
  check();
  const result = await client.push(item.submission);
  if (result.results.length !== item.submission.operations.length) throw new ApiError('Some snags could not be confirmed. Retry this checklist.', 503);
  const rejected = result.results.find((entry) => !['applied','already_applied'].includes(entry.status));
  if (rejected) throw new ApiError(rejected.message, 409);
  for (const [index,media] of item.media.entries()) {
    const operationId=item.submission.operations[index]?.operationId;
    const snagId = result.results.find(entry=>entry.operationId===operationId)?.entityId;
    if (!snagId) throw new ApiError('Snag confirmation is missing. Retry this checklist.',503);
    const capturedAt = item.submission.operations[index]?.kind === 'create_snag' ? (item.submission.operations[index] as Extract<SyncPush['operations'][number], {kind:'create_snag'}>).payload.capturedAt : new Date().toISOString();
    check();
    if(media.drawingNote?.trim()) await client.snagComments(snagId,{text:media.drawingNote.trim(),clientId:media.photoClientId});
    check();
    if (media.photo) await client.uploadEvidence({clientId:media.photoClientId, projectId:item.projectId, snagId, kind:'issue', capturedAt, file:media.photo});
    check();
    if (media.video) await client.uploadEvidence({clientId:media.videoClientId, projectId:item.projectId, snagId, kind:'issue_video', capturedAt, file:media.video});
  }
}
export async function sendSnagAction(item: QueuedSnagAction, active: () => boolean) {
  const check = () => { if (!active()) throw new ApiError('The sending account changed. Sign in with the original account.', 401); };
  check();
  // Completion evidence must exist before the server accepts ready-to-inspect.
  // Every identifier is saved before sending and reused after a lost response.
  const pin = item.pin ? await api.updateDrawingReference(item.snagId,item.pin) : null;
  check();
  const uploaded = [];
  for (const media of item.media) { check(); const evidence = await api.uploadEvidence({ ...media, projectId: item.projectId, snagId: item.snagId }); uploaded.push(evidence); await cacheValue(item.ownerId, `/v1/evidence/${evidence.id}/file`, media.file); }
  check();
  const result = item.submission.operations.length ? await api.push(item.submission) : { results: [] };
  for (const operation of item.submission.operations) {
    const entry = result.results.find(value => value.operationId === operation.operationId);
    if (!entry) throw new ApiError('The server did not confirm this update.', 503);
    if (!['applied', 'already_applied'].includes(entry.status)) throw new ApiError(entry.message, entry.status === 'conflict' ? 409 : 422);
  }
  check();
  if (item.comment) {
    const attachmentIds: string[] = [];
    for (const attachment of item.comment.attachments ?? []) {
      check();
      const evidence = await api.uploadEvidence({...attachment, projectId:item.projectId, snagId:item.snagId, kind:'comment_attachment'});
      uploaded.push(evidence); attachmentIds.push(evidence.id);
      await cacheValue(item.ownerId, `/v1/evidence/${evidence.id}/file`, attachment.file);
    }
    check();
    await api.snagComments(item.snagId, {text:item.comment.text, clientId:item.comment.clientId, attachmentIds});
  }
  // Retain confirmed status in the local snapshot even if the following screen
  // refresh loses its connection. Future server snapshots remain authoritative.
  const path = `/v1/projects/${item.projectId}/bootstrap`;
  const cached = await cachedValue<Bootstrap>(item.ownerId, path);
  if (cached && active()) {
    const pinned = cached.snags.find(value=>value.id===item.snagId);
    if(pin && pinned && pinned.version<=pin.version) Object.assign(pinned,{drawingId:pin.drawingId,drawingPage:pin.drawingPage,markupX:pin.markupX,markupY:pin.markupY,version:pin.version,updatedAt:pin.updatedAt});
    for (const evidence of uploaded) if (!cached.evidence.some(value => value.id === evidence.id)) cached.evidence.push(evidence);
    for (const operation of item.submission.operations) {
      if (operation.kind !== 'change_status') continue;
      const snag = cached.snags.find(value => value.id === item.snagId);
      const confirmed = result.results.find(value => value.operationId === operation.operationId);
      if (snag && confirmed?.serverVersion && snag.version <= confirmed.serverVersion) {
        snag.status = operation.payload.status; snag.version = confirmed.serverVersion;
        snag.updatedAt = operation.payload.occurredAt;
        if (operation.payload.status === 'ready_to_inspect') snag.completionNote = operation.payload.note;
        else if (operation.payload.note) snag.managerNote = operation.payload.note;
      }
    }
    await cacheValue(item.ownerId, path, cached);
  }
}
const processing = new Map<string, Promise<void>>();
export function flushSnagQueue(ownerId: string, onSent: (name: string) => void, active: () => boolean = () => true, retryNow = false) {
  if ((api.isViewingAs() && ownerId !== api.workOwner(api.currentOwner())) || (!api.isViewingAs() && ownerId.startsWith("view-as|"))) return Promise.resolve();
  const running = processing.get(ownerId); if (running) return running;
  const send = async () => {
    const blockedSnags = new Set<string>();
    for (const record of await queuedWork(ownerId)) {
      if (!active() || !navigator.onLine) break;
      const current = await loadAuditDraft<QueuedWork>(record.projectId);
      if (!current) continue;
      const item = current.payload;
      if (item.version !== 3 && blockedSnags.has(item.snagId)) continue;
      if (item.state !== 'pending' || !retryNow && (item.retryAt ?? 0) > Date.now()) { if (item.version !== 3) blockedSnags.add(item.snagId); continue; }
      const identity=api.sessionIdentity();
      try {
        const me = await api.me(true); if (!active() || api.workOwner(me.id) !== ownerId) break;
        const sameAccount = () => active() && identity === api.sessionIdentity() && api.workOwner(me.id) === ownerId;
        if (item.version === 3) await issueChecklist(item, api, sameAccount);
        else await sendSnagAction(item, sameAccount);
        await deleteAuditDraft(record.projectId);
        if (active()) { try { onSent(item.projectName); } catch { /* Delivery is confirmed even if the screen refresh fails. */ } }
      } catch (reason) {
        if (!active() || identity !== api.sessionIdentity()) break;
        const retryable = isNetworkFailure(reason) || reason instanceof ApiError && ([408, 423, 429].includes(reason.status) || reason.status >= 500);
        const attempts = (item.attempts ?? 0) + 1;
        const detail = reason instanceof Error ? reason.message : 'This update could not be sent.';
        await saveAuditDraft(record.projectId, { ...item, state: retryable ? 'pending' : 'failed', attempts,
          retryAt: retryable ? Date.now() + Math.min(300_000, 15_000 * 2 ** Math.min(attempts - 1, 5)) : 0,
          message: `${detail}${reason instanceof ApiError ? ` (HTTP ${reason.status})` : ''}${retryable ? ' Saved here; retrying automatically.' : ' Saved here. Review this message before retrying.'}` });
        if (item.version !== 3) blockedSnags.add(item.snagId);
        if (isNetworkFailure(reason) || reason instanceof ApiError && [401, 423, 429].includes(reason.status)) break;
      }
    }
    window.dispatchEvent(new Event('snag-queue-changed'));
  };
  // Web Locks prevents two open tabs from racing the same saved operations.
  const job = Promise.resolve(navigator.locks ? navigator.locks.request(`site-snag-send:${ownerId}`, send) : send()).then(() => undefined).finally(() => { processing.delete(ownerId); });
  processing.set(ownerId, job);
  return job;
}
